/**
 * GEX-Ray Widget
 *
 * Uses the V3 backend API with latest gamma values.
 *
 * Key features:
 * - Uses latest gamma (not per-trade gamma) for accurate GEX
 * - Filters to next expiration only (0DTE for indices)
 * - Simpler columnar data format optimized for Plotly
 * - Timestamps in milliseconds for proper timezone handling
 * - Historical mode outside market hours
 * - Live forming-bar OHLC from QF (~1s, final at minute close)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { usePlotly } from '../hooks/usePlotly';
import { useGexRay } from '../hooks/useGexRay';
import { useMarketOpen } from '../hooks/useMarketOpen';
import { useUpgradeAction } from '../hooks/useUpgradeAction';
import { tickerNotInPlanFrom } from './tickerNotInPlan';
import { profileService } from '../services/profileService';
import { qfSubscribe } from '../services/qfClient';
import { TickerLinkButton } from './TickerLinkButton';
import { useTickerLink } from '../contexts/TickerLinkContext';
import { createCoalescedScheduler } from '../utils/scheduleUpdate';
import type { GexRayBetaV3Settings } from '../types/gexSnapshot';
import {
  isBeforeMarketOpen,
  isWeekend,
  getEffectiveMarketDate,
  formatDateForDisplay,
} from '../utils/marketDate';
import { getOptionsDisplayTicker, normalizeOptionsTicker, underlyingPriceTicker } from '../utils/tickerDisplay';

interface GexRayWidgetProps {
  windowId: string;
  onRemove: () => void;
}


// Colors matching the dark theme
const COLORS = {
  // Panel, not page. `--color-dark-surface` is the tool-window body;
  // `--color-dark-bg` (#0A0A0A) is what sits BEHIND the windows, so using it
  // here made the plot area read as a hole in the widget.
  background: '#161616',
  cardBg: '#161616',
  // Neutral white at 5%, so gridlines take the tint of whatever they sit on.
  // Was #1e2433, a blue-tinted grey that is not in the palette and read as a
  // faint blue cast across the plot.
  gridLines: 'rgba(255,255,255,0.05)',
  text: '#C7C2B8', // --color-text-muted. Was #9ca3af, stock Tailwind grey.

  /**
   * ⚠️ Candles are MONOCHROME on purpose — `--color-candle-up` /
   * `--color-candle-down` in index.css, which exist for exactly this.
   *
   * They were bull-blue and bear-red, the same two colours used for the call
   * and put walls directly below. Price and gamma then shared a vocabulary, so
   * a run of blue candles read as call-wall strength. Bone and grey leave blue
   * and red meaning one thing only.
   */
  candleUp: '#F4F2ED',
  candleDown: '#61615E',

  callWallMajor: '#1C76A2',
  putWallMajor: '#D8483B',
  callWallMinor: 'rgba(28,118,162,0.5)',
  putWallMinor: 'rgba(216,72,59,0.5)',
  positive: '#1C76A2',
  negative: '#D8483B',
};

const DEFAULT_SETTINGS: GexRayBetaV3Settings = {
  ticker: 'SPX',
  mode: 'naive',
  showMinorWalls: true,
  showNetGex: true,
  showProfile: true,
};

// Format large numbers
function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined || isNaN(num)) return '--';
  const absNum = Math.abs(num);
  if (absNum >= 1e9) return (num / 1e9).toFixed(1) + 'B';
  if (absNum >= 1e6) return (num / 1e6).toFixed(1) + 'M';
  if (absNum >= 1e3) return (num / 1e3).toFixed(1) + 'K';
  return num.toFixed(0);
}

// Check if after market close (16:00 ET)
function isAfterMarketClose(): boolean {
  const now = new Date();
  const etString = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
  const et = new Date(etString);
  return et.getHours() >= 16;
}

// Check if we should show historical data (before 9:30 or after 16:00 or weekend)
function shouldShowHistorical(): boolean {
  return isWeekend() || isBeforeMarketOpen() || isAfterMarketClose();
}

// Simple view toggle button. Active state is Accent Gold for all three —
// they previously took a per-button `color`, which used stock amber (#f59e0b)
// and stock blue (#3b82f6), both off-brand and inline so the palette sweep
// never reached them.
function ViewButton({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-0.5 text-xs rounded border transition-colors ${
        isActive
          ? 'bg-accent-yellow border-accent-yellow text-hot-tag-text font-semibold'
          : 'bg-transparent border-dark-border text-gray-500 hover:text-gray-300 hover:border-gray-600'
      }`}
    >
      {label}
    </button>
  );
}

export function GexRayWidget({ windowId, onRemove }: GexRayWidgetProps) {
  // Whether this pane is following the shared ticker. The box goes
  // read-only while it is — see the note on the input.
  const { isWindowLinked } = useTickerLink();
  const isTickerLinked = isWindowLinked(windowId);
  // Plotly
  const { Plotly, isLoading: plotlyLoading, error: plotlyError } = usePlotly();

  // Refs
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartInitializedRef = useRef(false);
  const isLoadedRef = useRef(false);
  const isDisposedRef = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Ref to always call the latest createChart (avoids stale closure issues with WebSocket callback)
  const createChartRef = useRef<() => void>(() => {});

  // Ref to always have latest gexData (avoids stale closure in createChart when called from WebSocket)
  const gexDataRef = useRef<typeof gexData>(null);

  // Fast OHLC override map (timestamp -> {open, high, low, close})
  // Used to merge fast WebSocket OHLC updates with slower GEX data
  const fastOhlcRef = useRef<Map<number, { open: number; high: number; low: number; close: number }>>(new Map());
  const chartSchedulerRef = useRef(createCoalescedScheduler(400));

  // Zoom state preservation - stores user's zoom/pan so it persists across data updates
  const zoomStateRef = useRef<{
    xaxis?: [string | number, string | number];
    yaxis?: [number, number];
  } | null>(null);

  // State
  const [settings, setSettings] = useState<GexRayBetaV3Settings>(DEFAULT_SETTINGS);
  const [tickerInput, setTickerInput] = useState(getOptionsDisplayTicker(DEFAULT_SETTINGS.ticker));

  // Historical mode state
  const [isHistorical, setIsHistorical] = useState(shouldShowHistorical());
  const [effectiveDate, setEffectiveDate] = useState(getEffectiveMarketDate());

  // Update historical state periodically (every minute)
  useEffect(() => {
    const checkHistorical = () => {
      setIsHistorical(shouldShowHistorical());
      setEffectiveDate(getEffectiveMarketDate());
    };

    const interval = setInterval(checkHistorical, 60000);
    return () => clearInterval(interval);
  }, []);

  // Footer stats
  const [footerStats, setFooterStats] = useState({
    netGex: '--',
    netGexValue: null as number | null,
    posGex: '--',
    negGex: '--',
  });

  // Load state from backend on mount
  useEffect(() => {
    const loadState = async () => {
      try {
        const response = await profileService.getFrontendState();
        const frontendState = response.frontend_state;

        const saved = frontendState?.gexrayWidgets?.[windowId] as GexRayBetaV3Settings | undefined;
        if (saved) {
          setSettings({ ...DEFAULT_SETTINGS, ...saved });
          setTickerInput(getOptionsDisplayTicker(saved.ticker || DEFAULT_SETTINGS.ticker));
        }
        isLoadedRef.current = true;
      } catch (err) {
        console.error('Failed to load GEX-Ray state:', err);
        isLoadedRef.current = true;
      }
    };
    loadState();
  }, [windowId]);

  // Save state to backend (debounced) - saves to gexrayWidgets key
  // Ref to track latest settings for flush-on-unmount
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  useEffect(() => {
    if (!isLoadedRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      saveTimeoutRef.current = null;
      try {
        await profileService.saveFrontendState({
          gexrayWidgets: {
            [windowId]: settings,
          },
        });
      } catch (err) {
        console.error('Failed to save GEX-Ray state:', err);
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [windowId, settings]);

  // Flush pending save on unmount (e.g., popout window closed)
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        profileService.saveFrontendState({
          gexrayWidgets: {
            [windowId]: settingsRef.current,
          },
        }).catch(() => {});
      }
    };
  }, [windowId]);

  // GEX Ray data via REST + WebSocket
  // onUpdate callback bypasses React's dependency cycle for immediate chart updates
  const {
    data: gexData,
    isLoading: dataLoading,
    error: dataError,
    isConnected,
    lastUpdate,
    refetch,
  } = useGexRay({
    ticker: settings.ticker,
    mode: settings.mode,
    date: effectiveDate,
    enabled: isLoadedRef.current,
    onUpdate: () => chartSchedulerRef.current.schedule(() => createChartRef.current()),
  });

  // Resolve the out-of-plan refusal once, shared across the upgrade UI.
  // Any 403 counts — see `tickerNotInPlan.ts` for why the payload is optional.
  const tickerNotInPlan = useMemo(
    () => (dataError ? tickerNotInPlanFrom(dataError, settings.ticker) : null),
    [dataError, settings.ticker]
  );

  // Zeroday users land in their existing customer's billing portal rather than
  // the public marketing page. `upgrade_url` comes from the backend's refusal,
  // so QF keeps control of where an unlinked customer is sent.
  const { onUpgrade, isRedirecting: isRedirectingToPortal } = useUpgradeAction(
    !!tickerNotInPlan,
    tickerNotInPlan?.upgrade_url
  );

  // Keep gexDataRef in sync (for use in createChart to avoid stale closure)
  gexDataRef.current = gexData;

  // Live forming-bar OHLC from QF Centrifugo (forming bar ~1s + final at minute close).
  // Price data keys off the underlying INDEX ticker (SPX), never the options root
  // (SPXW) — ohlc:SPXW:1m has no data, which is why the SPX family showed no candles.
  useEffect(() => {
    if (isHistorical) return;
    const root = underlyingPriceTicker(settings.ticker.toUpperCase());
    return qfSubscribe(`ohlc:${root}:1m`, (raw) => {
      const d = raw as { minute: string; open: number; high: number; low: number; close: number };
      // Match the history bucket axis (gexBetaService.toEpochMs): treat a bare
      // timestamp as UTC so the live key equals chartData.bucket[i] and the
      // fastOhlcRef merge below lands on the same minute.
      const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(d.minute) ? d.minute : `${d.minute}Z`;
      const ts = new Date(iso).getTime();
      const bar = { open: d.open, high: d.high, low: d.low, close: d.close };
      const cache = fastOhlcRef.current;
      cache.set(ts, bar);
      // Map insertion order is oldest-first; drop keys that are no longer
      // the forming bar or the last few minutes.
      while (cache.size > 8) {
        const oldest = cache.keys().next().value;
        if (oldest === undefined) break;
        cache.delete(oldest);
      }
      // Persist into the GEX series so the bar survives once it ages out of
      // the 8-key overlay. Profile ticks no longer write OHLC.
      const series = gexDataRef.current;
      if (series) {
        for (let i = series.bucket.length - 1; i >= 0; i--) {
          if (series.bucket[i] === ts) {
            series.ohlc.open[i] = bar.open;
            series.ohlc.high[i] = bar.high;
            series.ohlc.low[i] = bar.low;
            series.ohlc.close[i] = bar.close;
            break;
          }
        }
      }
      chartSchedulerRef.current.schedule(() => createChartRef.current());
    });
  }, [settings.ticker, isHistorical]);

  // Clear fast OHLC cache when ticker changes
  useEffect(() => {
    chartSchedulerRef.current.cancel();
    fastOhlcRef.current.clear();
  }, [settings.ticker]);

  // Reset chart at market open - flush previous day's data
  useMarketOpen(() => {
    // Flush all historical data immediately so there's no gap
    gexDataRef.current = null;
    chartSchedulerRef.current.cancel();
    fastOhlcRef.current.clear();
    chartInitializedRef.current = false;

    // Update state to trigger live mode
    setIsHistorical(false);
    setEffectiveDate(getEffectiveMarketDate());

    // Delay refetch to give the data pipeline time to produce the first bucket.
    // The stream processor needs ~60s after open to write the first minute bar.
    // WebSocket updates will fill in as they arrive in the meantime.
    setTimeout(() => refetch(), 60_000);
  });

  // Update footer stats when data changes
  useEffect(() => {
    if (!gexData?.profile?.net_gex?.length) {
      setFooterStats({ netGex: '--', netGexValue: null, posGex: '--', negGex: '--' });
      return;
    }

    let netGex = 0;
    let posGex = 0;
    let negGex = 0;

    gexData.profile.net_gex.forEach((gex) => {
      netGex += gex;
      if (gex > 0) posGex += gex;
      if (gex < 0) negGex += Math.abs(gex);
    });

    setFooterStats({
      netGex: formatNumber(netGex),
      netGexValue: netGex,
      posGex: formatNumber(posGex),
      negGex: formatNumber(negGex),
    });
  }, [gexData, lastUpdate]);

  // Create/update chart
  const createChart = useCallback(() => {
    // Read from ref to get latest data (avoids stale closure when called from WebSocket callback)
    const chartData = gexDataRef.current;
    if (!chartContainerRef.current || isDisposedRef.current || !Plotly || !chartData) return;

    const { showMinorWalls, showNetGex, showProfile } = settings;
    const traces: Plotly.Data[] = [];

    // Convert timestamps to Date objects for Plotly (handles timezone automatically)
    const timestamps = chartData.bucket.map((ts) => new Date(ts));

    // Merge fast OHLC updates with base data
    // Fast OHLC from WebSocket takes priority over slower GEX data OHLC
    const mergedOhlc = {
      open: chartData.ohlc.open.map((v, i) => {
        const fastData = fastOhlcRef.current.get(chartData.bucket[i]);
        return fastData ? fastData.open : v;
      }),
      high: chartData.ohlc.high.map((v, i) => {
        const fastData = fastOhlcRef.current.get(chartData.bucket[i]);
        return fastData ? fastData.high : v;
      }),
      low: chartData.ohlc.low.map((v, i) => {
        const fastData = fastOhlcRef.current.get(chartData.bucket[i]);
        return fastData ? fastData.low : v;
      }),
      close: chartData.ohlc.close.map((v, i) => {
        const fastData = fastOhlcRef.current.get(chartData.bucket[i]);
        return fastData ? fastData.close : v;
      }),
    };

    // OHLC candlestick chart (uses merged fast + slow data)
    if (timestamps.length > 0 && mergedOhlc.close.length > 0) {
      traces.push({
        type: 'candlestick',
        name: settings.ticker,
        x: timestamps,
        open: mergedOhlc.open,
        high: mergedOhlc.high,
        low: mergedOhlc.low,
        close: mergedOhlc.close,
        increasing: { line: { color: COLORS.candleUp }, fillcolor: COLORS.candleUp },
        decreasing: { line: { color: COLORS.candleDown }, fillcolor: COLORS.candleDown },
        xaxis: 'x',
        yaxis: 'y',
        showlegend: false,
        hoverinfo: 'x+y',
      } as Plotly.Data);
    }

    // Major call walls (call_wall_1 = top positive GEX strike)
    const callWall1 = chartData.walls.call_wall_1.filter((v): v is number => v !== null);
    if (callWall1.length > 0) {
      traces.push({
        type: 'scatter',
        name: 'Call Wall',
        x: timestamps.slice(0, chartData.walls.call_wall_1.length),
        y: chartData.walls.call_wall_1,
        mode: 'markers',
        marker: { color: COLORS.callWallMajor, size: 6 },
        xaxis: 'x',
        yaxis: 'y',
        showlegend: false,
        hovertemplate: 'Call Wall: %{y}<extra></extra>',
      });
    }

    // Major put walls (put_wall_1 = top negative GEX strike)
    const putWall1 = chartData.walls.put_wall_1.filter((v): v is number => v !== null);
    if (putWall1.length > 0) {
      traces.push({
        type: 'scatter',
        name: 'Put Wall',
        x: timestamps.slice(0, chartData.walls.put_wall_1.length),
        y: chartData.walls.put_wall_1,
        mode: 'markers',
        marker: { color: COLORS.putWallMajor, size: 6 },
        xaxis: 'x',
        yaxis: 'y',
        showlegend: false,
        hovertemplate: 'Put Wall: %{y}<extra></extra>',
      });
    }

    // Minor walls
    if (showMinorWalls) {
      const callWall2 = chartData.walls.call_wall_2.filter((v): v is number => v !== null);
      if (callWall2.length > 0) {
        traces.push({
          type: 'scatter',
          name: 'Call Wall 2',
          x: timestamps.slice(0, chartData.walls.call_wall_2.length),
          y: chartData.walls.call_wall_2,
          mode: 'markers',
          marker: {
            color: 'rgba(28,118,162,0.1)',
            symbol: 'line-ew',
            size: 5,
            line: { color: COLORS.callWallMinor, width: 1 },
          },
          xaxis: 'x',
          yaxis: 'y',
          showlegend: false,
        });
      }

      const putWall2 = chartData.walls.put_wall_2.filter((v): v is number => v !== null);
      if (putWall2.length > 0) {
        traces.push({
          type: 'scatter',
          name: 'Put Wall 2',
          x: timestamps.slice(0, chartData.walls.put_wall_2.length),
          y: chartData.walls.put_wall_2,
          mode: 'markers',
          marker: {
            color: 'rgba(216,72,59,0.1)',
            size: 5,
            symbol: 'line-ew',
            line: { color: COLORS.putWallMinor, width: 1 },
          },
          xaxis: 'x',
          yaxis: 'y',
          showlegend: false,
        });
      }
    }

    // Net GEX bars - overlaid on candlestick chart
    // Use absolute values for bar heights (like V3.1), color by sign
    let maxAbsGex = 0;
    if (showNetGex && chartData.net_gex.length > 0) {
      const absNetGex = chartData.net_gex.map((v) => Math.abs(v));
      maxAbsGex = Math.max(...absNetGex);

      traces.push({
        type: 'bar',
        name: 'Net GEX',
        x: timestamps,
        y: absNetGex,
        xaxis: 'x',
        yaxis: 'y3',
        marker: {
          color: chartData.net_gex.map((v) => (v >= 0 ? COLORS.positive : COLORS.negative)),
          opacity: 0.7,
        },
        showlegend: false,
        hovertemplate: 'Net GEX: %{customdata:,.0f}<extra></extra>',
        customdata: chartData.net_gex, // Original values for hover
      } as Plotly.Data);
    }

    // Profile bar chart
    if (showProfile && chartData.profile.strikes.length > 0) {
      const colors = chartData.profile.net_gex.map((val) =>
        val >= 0 ? COLORS.positive : COLORS.negative
      );
      traces.push({
        x: chartData.profile.net_gex,
        y: chartData.profile.strikes,
        type: 'bar',
        orientation: 'h',
        name: 'Current GEX',
        marker: { color: colors },
        width: 0.4,
        xaxis: 'x2',
        yaxis: 'y2',
        showlegend: false,
        hovertemplate: 'Strike: %{y}<br>GEX: %{x:,.0f}<extra></extra>',
      });
    }

    // Calculate price range (use high/low for candlestick wicks)
    let priceRange: [number, number] | undefined = undefined;
    const allPrices: number[] = [
      ...chartData.ohlc.high.filter((p): p is number => p !== null),
      ...chartData.ohlc.low.filter((p): p is number => p !== null),
    ];
    const call1Strikes = chartData.walls.call_wall_1.filter((s): s is number => s !== null);
    const put1Strikes = chartData.walls.put_wall_1.filter((s): s is number => s !== null);
    if (call1Strikes.length > 0) allPrices.push(...call1Strikes);
    if (put1Strikes.length > 0) allPrices.push(...put1Strikes);

    if (allPrices.length > 0) {
      const minPrice = Math.min(...allPrices);
      const maxPrice = Math.max(...allPrices);
      const padding = (maxPrice - minPrice) * 0.1;
      priceRange = [minPrice - padding, maxPrice + padding];
    }

    // Layout
    const gexrayDomain = showProfile ? [0, 0.68] : [0, 1];
    const profileDomain = showProfile ? [0.72, 1] : [0, 0];

    // Logo watermark. The Fuzion mark, matching Gamma Exposure and Net
    // Sweeper Flow — this was the last chart still carrying the pre-rebrand
    // WSJ asset.
    const images: Partial<Plotly.Image>[] = [
      {
        source: '/logo-watermark.png',
        xref: 'paper',
        yref: 'paper',
        x: 0.34,
        y: 0.5,
        sizex: 0.25,
        sizey: 0.25,
        xanchor: 'center',
        yanchor: 'middle',
        // 0.10, not 0.15. The Fuzion mark is bone on dark and reads heavier
        // than the old one at the same value, and a watermark that competes
        // with the data is worse than none.
        opacity: 0.10,
        layer: 'below',
      },
    ];

    // Apply saved zoom state if available, otherwise use computed range
    const savedZoom = zoomStateRef.current;

    const layout = {
      images,
      // Simple grid: 1 row, 1-2 columns (volume is overlaid, not separate subplot)
      grid: {
        rows: 1,
        columns: showProfile ? 2 : 1,
        pattern: 'independent',
        subplots: showProfile ? [['xy', 'x2y2']] : [['xy']],
      },
      dragmode: 'pan', // Enable click-and-drag panning
      xaxis: {
        domain: gexrayDomain,
        type: 'date',
        rangeslider: { visible: false },
        showgrid: true,
        gridcolor: COLORS.gridLines,
        color: COLORS.text,
        title: undefined,
        // Apply saved zoom state for x-axis
        ...(savedZoom?.xaxis && { range: savedZoom.xaxis }),
      },
      yaxis: {
        domain: [0, 1],
        title: undefined,
        showgrid: true,
        gridcolor: COLORS.gridLines,
        color: COLORS.text,
        side: 'right',
        // Use saved zoom if available, otherwise computed priceRange
        range: savedZoom?.yaxis || priceRange,
      },
      ...(showProfile && {
        xaxis2: {
          domain: profileDomain,
          title: undefined,
          showgrid: true,
          gridcolor: COLORS.gridLines,
          color: COLORS.text,
        },
        yaxis2: {
          domain: [0, 1],
          title: undefined,
          showgrid: true,
          gridcolor: COLORS.gridLines,
          color: COLORS.text,
          side: 'right',
          showticklabels: false,
          matches: 'y',  // Links range to yaxis
        },
      }),
      // Volume axis - overlaid on main chart, scaled to bottom 10%
      ...(showNetGex && {
        yaxis3: {
          overlaying: 'y',
          side: 'right',
          showgrid: false,
          showticklabels: false,
          range: [0, maxAbsGex * 10], // Bars fill ~10% of chart height
          fixedrange: true, // Don't zoom with price axis
        },
      }),
      showlegend: false,
      paper_bgcolor: COLORS.background,
      plot_bgcolor: COLORS.background, // Darker plot area (matching V3.1)
      margin: { l: 10, r: 50, t: 10, b: 30 },
      hovermode: 'closest',
      autosize: true,
    } as unknown as Partial<Plotly.Layout>;

    const config: Partial<Plotly.Config> = {
      responsive: true,
      displayModeBar: false,
      scrollZoom: false,
    };

    const chartDiv = chartContainerRef.current;

    if (!chartInitializedRef.current) {
      Plotly.newPlot(chartDiv, traces, layout, config)
        .then(() => {
          chartInitializedRef.current = true;

          // Add relayout listener to capture zoom/pan state
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (chartDiv as any).on('plotly_relayout', (eventdata: Record<string, unknown>) => {
            // User zoomed or panned - save the new ranges
            if (eventdata['xaxis.range[0]'] !== undefined || eventdata['yaxis.range[0]'] !== undefined) {
              zoomStateRef.current = {
                ...(eventdata['xaxis.range[0]'] !== undefined && {
                  xaxis: [eventdata['xaxis.range[0]'], eventdata['xaxis.range[1]']] as [string | number, string | number],
                }),
                ...(eventdata['yaxis.range[0]'] !== undefined && {
                  yaxis: [eventdata['yaxis.range[0]'], eventdata['yaxis.range[1]']] as [number, number],
                }),
                // Preserve any existing zoom state for axes not being changed
                ...(!eventdata['xaxis.range[0]'] && zoomStateRef.current?.xaxis && { xaxis: zoomStateRef.current.xaxis }),
                ...(!eventdata['yaxis.range[0]'] && zoomStateRef.current?.yaxis && { yaxis: zoomStateRef.current.yaxis }),
              };
            }
            // User double-clicked to reset zoom (autorange)
            if (eventdata['xaxis.autorange'] === true || eventdata['yaxis.autorange'] === true) {
              zoomStateRef.current = null;
            }
          });
        })
        .catch((err: unknown) => {
          console.error('Error creating chart:', err);
        });
    } else {
      Plotly.react(chartDiv, traces, layout, config).catch((err: unknown) => {
        console.error('Error updating chart:', err);
      });
    }
  }, [Plotly, settings]); // Note: reads from gexDataRef.current and fastOhlcRef.current

  createChartRef.current = createChart;

  // REST seed / Plotly ready / settings: draw now. Live ticks use the
  // coalescer so they cannot steal this first paint.
  useEffect(() => {
    if (gexData && Plotly && !plotlyLoading) {
      createChart();
    }
  }, [gexData, Plotly, plotlyLoading, settings, createChart]);

  // Handle resize
  useEffect(() => {
    if (!chartContainerRef.current || !Plotly) return;

    const resizeObserver = new ResizeObserver(() => {
      if (!isDisposedRef.current && chartContainerRef.current && chartInitializedRef.current) {
        Plotly.Plots.resize(chartContainerRef.current);
      }
    });

    resizeObserver.observe(chartContainerRef.current);
    return () => resizeObserver.disconnect();
  }, [Plotly]);

  // Cleanup on unmount
  useEffect(() => {
    isDisposedRef.current = false;
    const chartContainer = chartContainerRef.current;
    const scheduler = chartSchedulerRef.current;

    return () => {
      isDisposedRef.current = true;
      scheduler.cancel();
      if (chartContainer && Plotly) {
        Plotly.purge(chartContainer);
      }
    };
  }, [Plotly]);

  // Handle ticker selection
  const handleTickerSelect = (ticker: string) => {
    const normalized = normalizeOptionsTicker(ticker.toUpperCase());
    setSettings((prev) => ({ ...prev, ticker: normalized }));
    setTickerInput(getOptionsDisplayTicker(normalized));
    chartInitializedRef.current = false;
    zoomStateRef.current = null; // Reset zoom on ticker change
    fastOhlcRef.current.clear(); // Clear fast OHLC cache
  };

  // Handle ticker input submit
  const handleTickerSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tickerInput.trim()) {
      handleTickerSelect(tickerInput.trim());
    }
  };

  // Toggle settings
  const toggleMinorWalls = () => {
    setSettings((prev) => ({ ...prev, showMinorWalls: !prev.showMinorWalls }));
  };

  const toggleNetGex = () => {
    setSettings((prev) => ({ ...prev, showNetGex: !prev.showNetGex }));
  };

  const toggleProfile = () => {
    setSettings((prev) => ({ ...prev, showProfile: !prev.showProfile }));
  };

  // Derive latest wall strikes from data
  const wallLevels = useMemo(() => {
    if (!gexData) return null;
    // Walk backwards to find the latest non-null wall values
    let callWall: number | null = null;
    let putWall: number | null = null;
    for (let i = gexData.walls.call_wall_1.length - 1; i >= 0; i--) {
      if (callWall === null && gexData.walls.call_wall_1[i] !== null) {
        callWall = gexData.walls.call_wall_1[i];
      }
      if (putWall === null && gexData.walls.put_wall_1[i] !== null) {
        putWall = gexData.walls.put_wall_1[i];
      }
      if (callWall !== null && putWall !== null) break;
    }
    return callWall !== null || putWall !== null ? { callWall, putWall } : null;
  }, [gexData, lastUpdate]);

  // Loading state
  if (plotlyLoading) {
    return (
      <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
        <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">GEX-Ray</h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <span className="text-gray-400 text-sm">Loading chart library...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (plotlyError) {
    return (
      <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
        <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">GEX-Ray</h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <span className="text-red-400 text-sm">Error loading chart library</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Header */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
            GEX-Ray — <span className="text-accent-yellow">{getOptionsDisplayTicker(settings.ticker)}</span>
          </h3>
          {isHistorical && (
            <span className="px-1.5 py-0.5 text-xs font-semibold uppercase tracking-[0.5px] bg-accent-yellow text-hot-tag-text rounded">
              {formatDateForDisplay(effectiveDate)}
            </span>
          )}
        </div>
        {/* Connection status: dot removed per brand direction; the offline
            label is kept so a dropped feed is still visible. */}
        {!isConnected && (
          <span className="text-xs text-gray-500" title="Disconnected">offline</span>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-1 bg-dark-bg/50 border-b border-dark-border relative z-20">
        {/* Ticker input and link button. Type + Enter; no autocomplete. */}
        <div className="flex items-center gap-1">
          <div className="relative">
            <form onSubmit={handleTickerSubmit} className="flex items-center gap-1">
              <input
                ref={inputRef}
                type="text"
                value={tickerInput}
                onChange={(e) => {
                  setTickerInput(e.target.value.toUpperCase());
                }}
                onFocus={() => {
                  // Clearing on focus is for typing a replacement. While
                  // linked there is nothing to type, and blanking the box
                  // would hide the very symbol the border is advertising.
                  if (isTickerLinked) return;
                  setTickerInput('');
                }}
                placeholder="Ticker"
                readOnly={isTickerLinked}
                title={isTickerLinked ? 'Linked — set the ticker from another linked widget or unlink to edit' : undefined}
/**
                 ⚠️ Read-only while linked. The symbol is owned by the link
                 group, so editing it here would either be silently discarded
                 or would move every other linked pane — neither of which a
                 plain-looking text field leads anyone to expect. The gold
                 border is the tell. */
                className={`w-20 px-2 py-0.5 text-xs border rounded text-accent-yellow font-semibold tracking-wide placeholder:font-normal placeholder:tracking-normal placeholder-gray-500 focus:outline-none ${
                  isTickerLinked
                    ? 'bg-dark-surface border-accent-yellow ring-1 ring-accent-yellow/40 cursor-default select-none'
                    : 'bg-dark-surface border-dark-border focus:border-accent-yellow'
                }`}
              />
            </form>
          </div>
          <TickerLinkButton
            windowId={windowId}
            currentTicker={settings.ticker}
            onTickerChange={handleTickerSelect}
          />
        </div>

        {/* Toggle buttons */}
        <div className="flex items-center gap-1">
          <ViewButton label="Minor" isActive={settings.showMinorWalls} onClick={toggleMinorWalls} />
          <ViewButton label="Vol" isActive={settings.showNetGex} onClick={toggleNetGex} />
          <ViewButton label="Profile" isActive={settings.showProfile} onClick={toggleProfile} />
        </div>

        {/* Close button */}
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          x
        </button>
      </div>

      {/* Chart area */}
      <div className="flex-1 relative">
        {dataLoading && !gexData && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark-bg/50">
            <span className="text-gray-400 text-sm">Loading data...</span>
          </div>
        )}
        {dataError && tickerNotInPlan && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-dark-bg/95 gap-3 px-6 text-center">
            <svg
              className="w-12 h-12 text-accent-yellow"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M2.5 19h19v2h-19v-2zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06l-4.23 1.12-3.47-5.46a1.5 1.5 0 00-2.56 0L6.5 9.7l-4.23-1.12c-.8-.22-1.63.26-1.84 1.06-.21.8.26 1.63 1.06 1.84l4.53 1.2v6.32h12v-6.32l4.53-1.2c.8-.21 1.27-1.04 1.06-1.84z" />
            </svg>
            <h3 className="text-base font-semibold text-gray-100">
              {tickerNotInPlan.message}
            </h3>
            {tickerNotInPlan.allowed_tickers.length > 0 && (
              <p className="text-xs text-gray-400">
                Your plan includes: {tickerNotInPlan.allowed_tickers.join(', ')}
              </p>
            )}
            <button
              onClick={onUpgrade}
              disabled={isRedirectingToPortal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-bull-blue hover:bg-bull-blue/90 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {isRedirectingToPortal ? 'Redirecting...' : 'Upgrade to FZN Pro'}
            </button>
          </div>
        )}
        {dataError && !tickerNotInPlan && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-dark-bg/50 gap-2">
            {(isBeforeMarketOpen() || isWeekend()) ? (
              <>
                <span className="text-accent-yellow text-sm font-medium">
                  No data yet for today
                </span>
                <span className="text-gray-400 text-xs">
                  {isWeekend() ? 'Markets are closed on weekends' : 'Market opens at 9:30 AM ET'}
                </span>
              </>
            ) : (
              <span className="text-red-400 text-sm">
                {(dataError as Error).message || 'Error loading data'}
              </span>
            )}
          </div>
        )}
        <div ref={chartContainerRef} className="w-full h-full" />

        {/* Wall levels legend */}
        {wallLevels && (
          <div className="absolute top-2 left-2 pointer-events-none rounded-md border border-accent-yellow bg-dark-surface/90 px-2.5 py-1.5 text-[11px]">
            {wallLevels.callWall !== null && (
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-sm flex-shrink-0 bg-bull-blue" />
                <span className="text-gray-400">Call Wall</span>
                <span className="ml-auto pl-4 font-numeric font-semibold text-bull-blue">
                  {wallLevels.callWall.toLocaleString()}
                </span>
              </div>
            )}
            {wallLevels.putWall !== null && (
              <div className="flex items-center gap-2 mt-1">
                <span className="w-2 h-2 rounded-sm flex-shrink-0 bg-bear-red" />
                <span className="text-gray-400">Put Wall</span>
                <span className="ml-auto pl-4 font-numeric font-semibold text-bear-red">
                  {wallLevels.putWall.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer stats */}
      <div className="flex items-center justify-around px-3 py-1 bg-dark-bg/50 border-t border-dark-border text-xs">
        <span className="text-gray-400">
          Net:{' '}
          <span
            className={
              footerStats.netGexValue === null
                ? 'font-numeric text-white'
                : footerStats.netGexValue >= 0
                  ? 'font-numeric text-bull-blue'
                  : 'font-numeric text-bear-red'
            }
          >
            {footerStats.netGex}
          </span>
        </span>
        <span className="text-gray-400">
          +GEX: <span className="font-numeric text-bull-blue">{footerStats.posGex}</span>
        </span>
        <span className="text-gray-400">
          -GEX: <span className="font-numeric text-bear-red">{footerStats.negGex}</span>
        </span>
      </div>
    </div>
  );
}
